import { Router } from '@angular/router';

/**
 * Helpers shared by the standard report screen and its weekly-report (mail link) twin:
 * date formatting, the viewer date range, the "requested report" payload and the
 * detailed-report prompt.
 */

/**
 * Formats a date as `dd-MM-yyyy` (local calendar date), the format the standard report
 * API expects for `start_date_detail` / `end_date_detail`.
 * @param date - Date to format; a falsy value gives `''`.
 * @returns The formatted date, or `''` when no date is given.
 */
export function formatReportDate(date: Date | null): string {
  if (!date) return '';
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

/**
 * Formats a Date or date-like string as a local `YYYY-MM-DD` string (the report viewer's format).
 * A `Date` instance is used as is; a string is parsed and an unparseable one gives `''`.
 * @param date - Date value to format.
 * @returns The `YYYY-MM-DD` string, or `''` for missing / invalid input.
 */
export function formatLocalReportDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  let d: Date;
  if (date instanceof Date) {
    d = date;
  } else {
    d = new Date(date);
    if (isNaN(d.getTime())) return '';
  }
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Resolves the report viewer's date range: the selected dates, or by default the start of
 * the current financial year (1 April; last year's when today is Jan–Mar) up to today.
 * @param startDate - Selected start date, if any.
 * @param endDate - Selected end date, if any.
 * @param today - The current date (injected for tests).
 * @returns `SD` / `ED` formatted by {@link formatLocalReportDate}.
 */
export function resolveReportDateRange(
  startDate: Date | null,
  endDate: Date | null,
  today: Date = new Date()
): { SD: string; ED: string } {
  const SD = startDate
    ? formatLocalReportDate(startDate)
    : formatLocalReportDate(
        today.getMonth() < 3
          ? new Date(today.getFullYear() - 1, 3, 1)
          : new Date(today.getFullYear(), 3, 1)
      );

  const ED = endDate
    ? formatLocalReportDate(endDate)
    : formatLocalReportDate(today);

  return { SD, ED };
}

/** Logged-in user fields sent with a requested (e-mailed) report. */
export interface RequestedReportUser {
  getCustomerId(): number;
  getCustomerName(): string;
  getRole(): string;
  getUserId(): number;
}

/** Screen filters sent with a requested (e-mailed) report. */
export interface RequestedReportFilters {
  startDate: Date | null;
  endDate: Date | null;
  /** Selected report category: `'S'` statutory or `'I'` internal. */
  status: 'S' | 'I';
  /** Report type, e.g. `'Detailed'`. */
  type: string;
  /** Location label (the standard screen's selected location; `''` on the weekly screen). */
  location: string;
}

/** Payload of `StandardReportService.downloadRequestedReport`. */
export interface RequestedReportParams {
  customer_id: number;
  customer_name: string;
  end_date_detail: string;
  flag_is_app: string;
  is_email_request_flag: true;
  location: string;
  report_type: string;
  start_date_detail: string;
  status_flag: 'S' | 'I';
  user_id: number;
}

/**
 * Builds the payload that asks the server to e-mail a report. Keys are in the order the
 * screens always sent them (the payload is HMAC-signed as serialized).
 * @param user - Source of the logged-in user's identity (the `UserService`).
 * @param filters - The screen's dates, category, report type and location label.
 * @returns The request payload.
 */
export function requestedReportParams(user: RequestedReportUser, filters: RequestedReportFilters): RequestedReportParams {
  return {
    customer_id: user.getCustomerId(),
    customer_name: user.getCustomerName(),
    end_date_detail: formatReportDate(filters.endDate),
    flag_is_app: user.getRole(),
    is_email_request_flag: true,
    location: filters.location,
    report_type: filters.type,
    start_date_detail: formatReportDate(filters.startDate),
    status_flag: filters.status,
    user_id: user.getUserId()
  };
}

/**
 * Picks the alert shown when the user asks for the detailed report: a "select both dates"
 * message when a date is missing, otherwise the "too many records, it will be e-mailed"
 * confirmation.
 * @param startDate - Selected start date.
 * @param endDate - Selected end date.
 * @param missingDatesVisible - `alertVisible` of the missing-dates message (differs per screen).
 * @returns The new `alertData` for the screen.
 */
export function detailReportAlert(
  startDate: Date | null,
  endDate: Date | null,
  missingDatesVisible: string
): { alertMessage: string; alertVisible: string } {
  if (!startDate || !endDate) {
    return { alertMessage: 'alerts.please_select_both_start_date_and_end_date_before', alertVisible: missingDatesVisible };
  }
  return { alertMessage: 'alerts.too_many_records_to_display_an_excel_file_will', alertVisible: 'confirm' };
}

/** The parts of `StandardReportService` used to hand a report over to the "show all" screen. */
export interface ShowAllReportStore {
  setPdfName(name: string): void;
  setComplianceFlag(flag: string): void;
}

/**
 * Hands a report over to the "show all standard report" screen and navigates there.
 * @param service - Stores the report flag and compliance type for the target screen.
 * @param router - Router used for the navigation.
 * @param reportFlag - Report flag (stored as the PDF name).
 * @param complianceType - Compliance type flag.
 * @returns {void} Side effects: writes to the service and navigates.
 */
export function openShowAllStandardReport(
  service: ShowAllReportStore,
  router: Router,
  reportFlag: string,
  complianceType: string
): void {
  service.setPdfName(reportFlag);
  service.setComplianceFlag(complianceType);
  router.navigate(['/showAllStandardReport']);
}
